/* eslint-disable fp/no-mutation */
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Loading from 'src/v3/components/loading'
import { selectUseEvents } from 'src/v3/settings/settings.slice'
import Survey, { SurveyFormValues } from 'src/v3/components/survey'
import {
  selectEventById,
  selectEventsStatus,
  fetchEvents,
  selectCompletionEventsByID,
  selectCompletionEventsStatusByID,
  fetchEventCompletionEvents,
  selectQuizByEvent,
  selectQuizStatusByEvent,
  fetchQuiz,
  selectQuizSessionResults,
  selectQuizSessionResultsStatus,
  fetchQuizSessionResult,
  selectEventCERecordsStatus,
  selectEventCERecords,
  fetchEventCERecords,
  selectAccreditationsByTypeByEvent,
  selectAccreditationsStatus,
  selectEventAccreditationsStatus,
  fetchAccreditations,
  fetchEventAccreditations,
} from './events.slice'
import {
  CertificateIcon,
  CloseIcon,
  ConfirmIcon,
  DownloadIcon,
  LeftArrowIcon,
  ResetIcon,
} from '../core/icons'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()
  let eventID = eventId || ''

  let useEvents = useAppSelector(selectUseEvents)

  let event = useAppSelector(selectEventById(eventID))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let sessionResults = useAppSelector(selectQuizSessionResults(eventID))
  let sessionResultsStatus = useAppSelector(
    selectQuizSessionResultsStatus(eventID),
  )
  let completionEvents = useAppSelector(selectCompletionEventsByID(eventID))
  let completionEventsStatus = useAppSelector(
    selectCompletionEventsStatusByID(eventID),
  )
  let ceRecords = useAppSelector(selectEventCERecords(eventID))
  let ceRecordsStatus = useAppSelector(selectEventCERecordsStatus(eventID))
  let accreditationsByType = useAppSelector(
    selectAccreditationsByTypeByEvent(eventID),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )

  let [ceRecordID, setCERecordID] = useState<string>()
  let [resetModal, setResetModal] = useState<boolean>(false)
  let [loading, setLoading] = useState<boolean>(false)
  let [downloading, setDownloading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  // Check if accreditations are ready for linked service events
  // Non-linked service events (FOAMfrat) don't need accreditations loaded
  let accreditationsReady =
    !event?.linkedServiceID ||
    (accreditationsStatus === 'fulfilled' &&
      eventAccreditationsStatus === 'fulfilled')

  // Wait for all data including accreditations before proceeding
  // This prevents race conditions where we might auto-submit CE without checking survey eligibility
  let allFulfilled =
    eventsStatus === 'fulfilled' &&
    completionEventsStatus === 'fulfilled' &&
    (sessionResultsStatus === 'fulfilled' ||
      sessionResultsStatus === 'rejected') &&
    ceRecordsStatus === 'fulfilled' &&
    (quizStatus === 'fulfilled' || quizStatus === 'rejected') &&
    accreditationsReady

  let hasQuiz = !!quiz
  let hasHours = (event?.hours || 0) > 0
  // Determine if this is a linked service event with "No Accreditation"
  // Such events should skip the survey even though they're linked services
  let isNotAccreditedLinkedService = () => {
    if (!event?.linkedServiceID) {
      return false
    }
    return (accreditationsByType?.notAccredited || []).length > 0
  }
  // Show survey for:
  // - FOAMfrat events (no linkedServiceID) with hours
  // - Linked service events with accreditation (e.g., "Accredited") and hours
  // Skip survey for:
  // - Events without hours
  // - Linked service events with "No Accreditation"
  let hasSurvey = !isNotAccreditedLinkedService() && hasHours

  let baseCompletion =
    completionEvents?.length > 0 && completionEvents?.[0].status !== 'restarted'
  let eventCompleted = hasQuiz
    ? baseCompletion && sessionResults?.passed
    : baseCompletion
  let showSurvey = hasSurvey && !ceRecords?.length && allFulfilled

  let subtitleText = showSurvey
    ? 'To get credit for this event, please fill out and submit the survey below.'
    : 'You may click the button below to download your certificate for this event.'
  if (loading) {
    subtitleText =
      'Please wait while we submit your CE and generate your certificate. This may take a few moments.'
  }

  let noCertificate = allFulfilled && (!eventCompleted || !hasHours)

  let ceRecordSubmit = () =>
    axios
      .post('/api/event/ce-record', {
        eventID,
        // passedQuizSessionID: completionEvents[0]?.sessionID,
      })
      .then(pluckData)
      .then((data) => setCERecordID(data?.id))
      .then(() => dispatch(fetchEventCERecords(eventID)))
      .then(() => Promise.resolve())
      .catch((err) => {
        setErrorMessage(
          err?.message || err?.data?.message || 'Error downloading certificate',
        )
      })

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (eventID && (!quizStatus || quizStatus === 'uninitialized')) {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  useEffect(() => {
    if (
      eventID &&
      (!sessionResultsStatus || sessionResultsStatus === 'uninitialized')
    ) {
      dispatch(fetchQuizSessionResult(eventID))
    }
  }, [sessionResultsStatus, eventID])

  useEffect(() => {
    if (
      eventID &&
      (!completionEventsStatus || completionEventsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEvents(eventID))
    }
  }, [completionEventsStatus, eventID])

  useEffect(() => {
    if (eventID && (!ceRecordsStatus || ceRecordsStatus === 'uninitialized')) {
      dispatch(fetchEventCERecords(eventID))
    } else if (ceRecordsStatus === 'fulfilled' && ceRecords.length) {
      setCERecordID(ceRecords[0].ceRecordID)
    }
  }, [ceRecordsStatus, eventID])

  // Fetch accreditations for linked service events to determine survey eligibility
  useEffect(() => {
    if (event?.linkedServiceID) {
      dispatch(fetchAccreditations())
      dispatch(fetchEventAccreditations())
    }
  }, [event?.linkedServiceID])

  // Auto-submit CE record for events without survey
  // Dependencies include accreditation statuses to prevent race conditions
  useEffect(() => {
    if (
      allFulfilled &&
      eventCompleted &&
      hasHours &&
      !hasSurvey &&
      !ceRecords.length
    ) {
      setLoading(true)
      ceRecordSubmit().finally(() => setLoading(false))
    }
  }, [
    eventsStatus,
    completionEventsStatus,
    sessionResultsStatus,
    ceRecordsStatus,
    accreditationsStatus,
    eventAccreditationsStatus,
  ])

  return useEvents ? (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <CertificateIcon />
        </Avatar>
        <Typography variant="h4">Event Certificate</Typography>
      </Box>
      {event?.banner && (
        <img
          style={{
            width: '100%',
            aspectRatio: '4 / 1',
            borderRadius: '10px',
            marginTop: 12,
            marginBottom: 12,
          }}
          src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.banner?.replace('originals', 'resized')}`}
        />
      )}
      <Card variant="outlined" sx={{ p: 3, mt: 2, mb: 3 }}>
        {noCertificate ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {!eventCompleted
                ? 'Event Not Completed'
                : 'Event Completion Recorded'}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ textAlign: 'center', maxWidth: 600 }}
            >
              {!eventCompleted
                ? 'You have not completed this event.'
                : `Thank you for attending ${event?.name}! We hope you enjoyed it. While this event does not have a certificate, your completion has been recorded.`}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<LeftArrowIcon />}
              onClick={() => navigate(`/events/${eventID}`)}
            >
              Back to Event
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              Congratulations on Completing {event?.name}!
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ textAlign: 'center', maxWidth: 600 }}
            >
              {subtitleText}
            </Typography>
            {loading && <LinearProgress sx={{ width: '100%' }} />}
            {ceRecordID && !loading && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<DownloadIcon />}
                  loading={downloading}
                  onClick={() => {
                    setDownloading(true)
                    new Promise((resolve) => {
                      setTimeout(resolve, 5000)
                    }).then(() =>
                      axios
                        .get(`/api/event/certificate?ceRecordID=${ceRecordID}`)
                        .then((response) => {
                          axios
                            .get(response.data, { responseType: 'blob' })
                            .then((resp) => {
                              let url = window.URL.createObjectURL(
                                new Blob([resp.data]),
                              )
                              let link = document.createElement('a')
                              link.href = url
                              link.setAttribute(
                                'download',
                                `${event?.name}.pdf`,
                              )
                              document.body.appendChild(link)
                              link.click()
                            })
                            .catch((err) => {
                              setErrorMessage(
                                err?.message ||
                                  err?.data?.message ||
                                  'Error downloading certificate',
                              )
                            })
                            .finally(() => setDownloading(false))
                        }),
                    )
                  }}
                  sx={{ px: 3, py: 1.5 }}
                >
                  Download Certificate
                </Button>
                {!event?.linkedServiceID && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ResetIcon />}
                    onClick={() => setResetModal(true)}
                  >
                    Restart Event Progress
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LeftArrowIcon />}
                  onClick={() => navigate(`/events/${eventID}`)}
                  sx={{ px: 3, py: 1.5 }}
                >
                  Back to Event
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Card>
      <Dialog onClose={() => setResetModal(false)} open={resetModal}>
        <Paper className="px-3 py-1" sx={{ maxWidth: 300 }}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            Confirm Event Restart
          </DialogTitle>
          <DialogContent className="px-2">
            Restarting an event allows you to attend this event again and get
            credit for your current certification schedule.
            <Box sx={{ height: 8 }}></Box>
            Upon completion, a new certificate will be generated. Are you sure
            you want to restart this event?
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3">
            <Button
              onClick={() => {
                setLoading(true)
                axios
                  .post('/api/event/restart-event', { eventID })
                  .then(() => dispatch(fetchEventCompletionEvents(eventID)))
                  .then(() => navigate(`/events/${eventID}`))
                  .catch((err) => setErrorMessage(pluckError(err)))
                  .finally(() => {
                    setResetModal(false)
                    setLoading(false)
                  })
              }}
              color="primary"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              loading={loading}
              startIcon={<ConfirmIcon />}
            >
              Confirm
            </Button>
            <Button
              onClick={() => setResetModal(false)}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
      {showSurvey && (
        <Survey
          onSubmit={async (values: SurveyFormValues) => {
            setLoading(true)
            return axios
              .post('/api/event/survey', { eventID, ...values })
              .then(() => (hasHours ? ceRecordSubmit() : Promise.resolve()))
              .catch((err) => {
                setErrorMessage(
                  err?.message ||
                    err?.data?.message ||
                    'Error submitting survey',
                )
              })
              .finally(() => setLoading(false))
          }}
        />
      )}
      <Loading
        entities={[
          eventsStatus,
          quizStatus,
          sessionResultsStatus,
          completionEventsStatus,
          ceRecordsStatus,
          // Only show loading for accreditations if this is a linked service event
          ...(event?.linkedServiceID
            ? [accreditationsStatus, eventAccreditationsStatus]
            : []),
        ]}
      />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  )
}
